import React, { Component } from 'react';
import fallbackImage from '../../img/Cubs_News_Bullseye_Final.png';
import Moment from 'moment';
import {
  contentfulItemAllowsExperience,
  getCurrentExperience,
} from '../../utils/visibility';

/* ---------------------------
   Utility helpers
---------------------------- */

function getExperience(preferred) {
  return getCurrentExperience('internal', preferred);
}

function getPath(obj, ...path) {
  let cur = obj;
  for (const p of path) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function normalizeSalesforceFromProps(props) {
  const looksLikeSalesforceItem = (it) => {
    if (!it || typeof it !== 'object') return false;
    if (it.publish_date || it.email_header) return true;
    if (Array.isArray(it.stories)) return true;
    // Salesforce detail ids are often present and route is not.
    if (it.id && !it.route) return true;
    return false;
  };

  const isSalesforceArray = (arr) =>
    Array.isArray(arr) && arr.length > 0 && arr.some((it) => looksLikeSalesforceItem(it));

  const candidates = [];

  // Prefer explicit Salesforce-shaped sources first.
  candidates.push(getPath(props, 'articles'));
  candidates.push(getPath(props, 'data', 'articles'));
  candidates.push(getPath(props, 'news', 'articles'));
  // Legacy fallbacks (may contain Contentful shapes, so keep last).
  candidates.push(getPath(props, 'news'));
  candidates.push(getPath(props, 'newsItems'));

  for (const c of candidates) {
    if (isSalesforceArray(c)) return c;
    if (c && isSalesforceArray(c.articles)) return c.articles;
  }

  return [];
}

/* ---------------------------
   Contentful finder heuristics
--------------------------- */

function looksLikeContentfulItem(it) {
  if (!it || typeof it !== 'object') return false;
  if (typeof it.newsTitle === 'string' && it.newsTitle.trim()) return true;
  if (typeof it.route === 'string' && it.route.trim()) return true;
  if (it.visibilityValue !== undefined) return true;
  if (getPath(it, 'image', 'url')) return true;
  return false;
}

function findContentfulNewsItemsInProps(props) {
  if (!props || typeof props !== 'object') return [];

  const candidatePaths = [
    ['homePageQuery', 'data', 'newsItems'],
    ['searchPageQuery', 'data', 'newsItems'],
    ['homePageQuery', 'newsItems'],
    ['searchPageQuery', 'newsItems'],
    ['data', 'newsItems'],
    ['newsItems'],
  ];

  // collect candidate arrays
  const arrays = [];
  for (const p of candidatePaths) {
    const v = getPath(props, ...p);
    if (Array.isArray(v) && v.length) arrays.push({ where: p.join('.'), arr: v });
    if (v && typeof v === 'object' && Array.isArray(v.newsItems) && v.newsItems.length) {
      arrays.push({ where: p.join('.') + '.newsItems', arr: v.newsItems });
    }
  }

  // Prefer arrays that contain at least one contentful-shaped item
  for (const cand of arrays) {
    if (cand.arr.some(looksLikeContentfulItem)) {
      return cand.arr;
    }
  }

  // Shallow DFS search for a property named "newsItems" that looks like Contentful
  const maxDepth = 3;
  const stack = [{ node: props, path: [] }];
  const visited = new WeakSet();

  while (stack.length) {
    const { node, path } = stack.pop();
    if (!node || typeof node !== 'object') continue;
    if (visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node.newsItems) && node.newsItems.length) {
      if (node.newsItems.some(looksLikeContentfulItem)) {
        return node.newsItems;
      }
    }

    if (path.length >= maxDepth) continue;

    for (const key of Object.keys(node)) {
      try {
        const child = node[key];
        if (child && typeof child === 'object') {
          stack.push({ node: child, path: path.concat(key) });
        }
      } catch (e) {
        // ignore odd getters / proxies
      }
    }
  }

  return [];
}

/* ---------------------------
   Visibility helpers
--------------------------- */

function normalizeToken(v) {
  return (v || '').toString().toLowerCase().trim();
}

function isVisibleToExperience(item, experience) {
  return contentfulItemAllowsExperience(item, experience);
}

/* ---------------------------
   Salesforce Renderer (Internal only)
--------------------------- */

function MapSalesforce({ articles }) {
  const arr = Array.isArray(articles) ? [...articles] : [];
  if (!arr.length) return null;

  const safeMs = (v) => {
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : 0;
  };

  const filtered = arr.filter((item) => {
    if (!item || typeof item !== 'object') return false;
    const hasTitle = !!(item.email_header || item.title || item.subject);
    const hasDate = !!(item.publish_date || item.date);
    const hasImage = !!(
      getPath(item, 'image', 'url') ||
      getPath(item, 'hero_image', 'url') ||
      item.hero_image
    );
    const hasStories = Array.isArray(item.stories) && item.stories.length > 0;
    return hasTitle || hasDate || hasImage || hasStories;
  });

  if (!filtered.length) return null;

  filtered.sort((a, b) => {
    const byDate = safeMs(b?.publish_date || b?.date) - safeMs(a?.publish_date || a?.date);
    if (byDate !== 0) return byDate;
    const aKey = normalizeToken(a?.email_header || a?.id || '');
    const bKey = normalizeToken(b?.email_header || b?.id || '');
    if (aKey < bKey) return -1;
    if (aKey > bKey) return 1;
    return 0;
  });

  const tiles = filtered.slice(0, 3).map((item, index) => {
    const id = item?.id;
    const href = id ? `/cubs-news/${id}` : '#';
    const date = item?.publish_date || item?.date;
    const title = item?.email_header || item?.title || item?.subject || 'Cubs News';
    const stories = Array.isArray(item?.stories) ? item.stories : [];
    const heroUrl =
      getPath(item, 'image', 'url') ||
      getPath(item, 'hero_image', 'url') ||
      item?.hero_image ||
      null;

    const imageUrl = heroUrl || fallbackImage;

    return (
      <li key={index}>
        <div className="col md-4 news">
          <a href={href}>
            <div className="news-img-container">
              <div
                className="news-img"
                role="img"
                aria-label={title}
                style={{ backgroundImage: `url(${imageUrl})` }}
              />
            </div>
          </a>

          <p>{date ? Moment.utc(date).format('MM/DD/YYYY') : ''}</p>

          <h3>
            <a href={href}>{title}</a>
          </h3>

          <div className="news-text">
            {stories.length ? stories.slice(0, 2).map((s, i) => <div key={i}>{s?.subject}</div>) : null}
          </div>
        </div>
      </li>
    );
  });

  return <ul>{tiles}</ul>;
}

/* ---------------------------
   Contentful Tiles Renderer
--------------------------- */

function renderContentfulTiles(items) {
  if (!items.length) return null;

  const tiles = items.slice(0, 3).map((item, index) => {
    const href = item?.route ? `/cubs-news-departments/${item.route}` : '#';
    const imageUrl = getPath(item, 'image', 'url') || fallbackImage;
    const title = item?.newsTitle || 'News';
    const date = item?.date;

    return (
      <li key={index}>
        <div className="col md-4 news">
          <a href={href}>
            <div className="news-img-container">
              <div
                className="news-img"
                role="img"
                aria-label={title}
                style={{ backgroundImage: `url(${imageUrl})` }}
              />
            </div>
          </a>

          <p>{date ? Moment(date).format('MM/DD/YYYY') : ''}</p>

          <h3>
            <a href={href}>{title}</a>
          </h3>
        </div>
      </li>
    );
  });

  return <ul>{tiles}</ul>;
}

/* ---------------------------
   Main Component
--------------------------- */

class News extends Component {
  constructor(props) {
    super(props);
    this.state = {
      sfArticles: [],
      loading: false,
      error: null,
    };
    this._isMounted = false;
    this._controller = null;
  }

  componentDidMount() {
    this._isMounted = true;

    const experience = getExperience(this.props.experience);
    const fromProps = normalizeSalesforceFromProps(this.props);

    if (experience === 'internal' && !fromProps.length) {
      this.fetchSalesforce();
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this._controller) this._controller.abort();
  }

  fetchSalesforce() {
    this._controller = new AbortController();
    this.setState({ loading: true });

    fetch('/news', {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: this._controller.signal,
    })
      .then((r) => r.json())
      .then((data) => {
        const articles = Array.isArray(data) ? data : Array.isArray(data?.articles) ? data.articles : [];
        if (this._isMounted) this.setState({ sfArticles: articles, loading: false });
      })
      .catch(() => {
        if (this._isMounted) this.setState({ loading: false, error: true });
      });
  }

  render() {
    const experience = getExperience(this.props.experience);

    // Salesforce
    const fromProps = normalizeSalesforceFromProps(this.props);
    const sfArticles = fromProps.length > 0 ? fromProps : this.state.sfArticles;
    const showSalesforce = experience === 'internal' && sfArticles.length > 0;

    // Contentful (prefer Contentful-shaped arrays)
    const contentfulNews = findContentfulNewsItemsInProps(this.props);

    const safeDateMs = (v) => {
      const ms = new Date(v).getTime();
      return Number.isFinite(ms) ? ms : 0;
    };

    const filteredContentful = (contentfulNews || [])
      .filter((item) => looksLikeContentfulItem(item)) // ensure shape
      .filter((item) => isVisibleToExperience(item, experience))
      .sort((a, b) => safeDateMs(b?.date) - safeDateMs(a?.date));

    const contentfulTiles = renderContentfulTiles(filteredContentful);
    const showContentful = experience === 'seasonal';

    return (
      <section className="section highlight-section">
        <header className="section-header row">
          <div className="col">
            <h2 className="section-title section-title-left">Cubs News</h2>
            <a href="/cubs-news" className="view-all">
              View all news
            </a>
          </div>
        </header>

        <div className="section-content row cubs-news">
          {/* Internal: Salesforce only */}
          {experience === 'internal' ? (
            <div style={{ width: '100%' }}>
              {showSalesforce ? (
                <MapSalesforce articles={sfArticles} />
              ) : (
                <div className="no-results">No intranet news available.</div>
              )}
            </div>
          ) : null}

          {/* Seasonal: Contentful only */}
          {showContentful ? (
            <div style={{ width: '100%' }}>
              {contentfulTiles ? (
                contentfulTiles
              ) : (
                <div className="no-results">No seasonal news available.</div>
              )}
            </div>
          ) : null}
        </div>
      </section>
    );
  }
}

export default News;
